import { useRef, useState, type CSSProperties } from "react";
import { useNavigate } from "@tanstack/react-router";
import { IconButton } from "@/components/ui/Button";
import { Icon } from "@/components/ui/Icon";
import { Popover } from "@/components/ui/Popover";
import { useNotifications } from "@/hooks/useNotifications";
import { BRAND } from "@/lib/tokens";
import { relPast } from "@/lib/format";
import { t } from "@/lib/strings";
import type {
  DeadlineApproachingPayload,
  FollowUpCreatedPayload,
  NotificationItem,
  RowAssignedPayload,
  RowCommentedPayload,
  RowSharedPayload,
  StatusChangedPayload,
  TicketArrivedPayload,
  UUID,
  MemoNotificationPayload,
} from "@/lib/types";

function isStatusChanged(n: NotificationItem): n is NotificationItem & {
  payload: StatusChangedPayload;
} {
  return n.kind === "row.status_changed";
}

function isRowCommented(n: NotificationItem): n is NotificationItem & {
  payload: RowCommentedPayload;
} {
  return n.kind === "row.commented";
}

function isRowAssigned(n: NotificationItem): n is NotificationItem & {
  payload: RowAssignedPayload;
} {
  return n.kind === "row.assigned";
}

/** All three deadline reminders (approaching / due today / overdue) share one payload shape;
 *  `days_remaining` tells them apart (negative = overdue). */
function isDeadlineReminder(n: NotificationItem): n is NotificationItem & {
  payload: DeadlineApproachingPayload;
} {
  return (
    n.kind === "row.deadline_approaching" ||
    n.kind === "row.deadline_due" ||
    n.kind === "row.deadline_overdue"
  );
}

function isTicketArrived(n: NotificationItem): n is NotificationItem & {
  payload: TicketArrivedPayload;
} {
  return n.kind === "row.ticket_arrived";
}

function isRowShared(n: NotificationItem): n is NotificationItem & {
  payload: RowSharedPayload;
} {
  return n.kind === "row.shared";
}

function isFollowUpCreated(n: NotificationItem): n is NotificationItem & {
  payload: FollowUpCreatedPayload;
} {
  return n.kind === "row.follow_up_created";
}

/** 🔴 The memo kinds had NO branch at all, so they fell through to the raw-`item.kind` fallback
 *  at the bottom of this file and the bell literally read "memo.finalized". Every other kind has
 *  a branch; these were added later and nobody came back here. */
function isMemoNotification(n: NotificationItem): n is NotificationItem & {
  payload: MemoNotificationPayload;
} {
  return n.kind === "memo.finalized" || n.kind === "memo.prep_requested";
}

function deadlineText(days: number): string {
  if (days < 0) return "überfällig";
  if (days === 0) return "heute fällig";
  if (days === 1) return "morgen fällig";
  return `in ${days} Tagen fällig`;
}

export function NotificationsDropdown() {
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const [open, setOpen] = useState(false);
  const { items, unreadCount, markRead, markAllRead, isLoading } = useNotifications();
  const navigate = useNavigate();

  function onPickNotification(item: NotificationItem) {
    if (!item.read_at) markRead.mutate(item.id);
    setOpen(false);
    // 🔴 A memo notification carries NO board_id (a memo is not a board), so the board branch
    // below silently did nothing and the click just closed the dropdown. The memo id rides in
    // the payload — deep-link from that, exactly as the email already does.
    if (isMemoNotification(item)) {
      void navigate({
        to: "/memo/$memoId",
        params: { memoId: item.payload.memo_id as UUID },
      });
      return;
    }
    if (item.board_id) {
      void navigate({
        to: "/b/$boardId",
        params: { boardId: item.board_id as UUID },
        search: item.row_id ? { row: item.row_id } : {},
      });
    }
  }

  return (
    <div style={{ position: "relative" }}>
      <IconButton
        ref={triggerRef}
        icon="bell"
        size={32}
        color="rgba(255,255,255,0.85)"
        title={t.notificationsTitle}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setOpen((v) => !v);
        }}
      />
      {unreadCount > 0 ? (
        <span
          style={{
            position: "absolute",
            top: 2,
            right: 2,
            minWidth: 16,
            height: 16,
            padding: "0 4px",
            borderRadius: 999,
            background: BRAND.orange,
            color: "#fff",
            fontSize: 10,
            fontWeight: 700,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            fontFamily: "Inter Variable, Inter, system-ui, sans-serif",
            fontVariantNumeric: "tabular-nums",
            lineHeight: 1,
            pointerEvents: "none",
          }}
        >
          {unreadCount > 99 ? "99+" : unreadCount}
        </span>
      ) : null}
      {open ? (
        <Popover
          anchor={triggerRef.current}
          onClose={() => setOpen(false)}
          width={380}
          align="right"
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              padding: "10px 14px 8px",
              borderBottom: "1px solid var(--hairline-soft)",
            }}
          >
            <span style={{ fontSize: 13, fontWeight: 600, color: "var(--ink-1)", flex: 1 }}>
              {t.notificationsTitle}
            </span>
            {unreadCount > 0 ? (
              <button
                type="button"
                onClick={() => markAllRead.mutate()}
                style={{
                  background: "transparent",
                  border: "none",
                  color: "var(--ink-3)",
                  fontSize: 11.5,
                  cursor: "pointer",
                  padding: "4px 6px",
                  borderRadius: 4,
                  fontFamily: "Inter Variable, Inter, system-ui, sans-serif",
                }}
              >
                {t.notificationsMarkAllRead}
              </button>
            ) : null}
          </div>
          <div style={{ maxHeight: 420, overflow: "auto" }}>
            {isLoading ? (
              <Empty label={t.loading} />
            ) : items.length === 0 ? (
              <Empty label={t.notificationsEmpty} />
            ) : (
              items.map((n) => (
                <NotificationRow key={n.id} item={n} onClick={() => onPickNotification(n)} />
              ))
            )}
          </div>
        </Popover>
      ) : null}
    </div>
  );
}

function NotificationRow({ item, onClick }: { item: NotificationItem; onClick: () => void }) {
  const unread = !item.read_at;
  if (isStatusChanged(item)) {
    const p = item.payload;
    return (
      <button
        type="button"
        onClick={onClick}
        style={rowStyle(unread)}
        onMouseEnter={(e) => (e.currentTarget.style.background = "var(--surface-soft)")}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = unread ? "var(--surface-soft)" : "transparent";
        }}
      >
        <Icon name="status" size={15} color={unread ? "var(--accent)" : "var(--ink-3)"} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={titleStyle}>
            <span style={{ fontWeight: unread ? 500 : 400 }}>
              {t.notificationStatusChanged}: {p.row_title || "Anfrage"}
            </span>
          </div>
          <div style={subStyle}>
            {p.from_status_label ? `${p.from_status_label} → ` : ""}
            <strong style={{ color: "var(--ink-1)", fontWeight: 600 }}>{p.to_status_label}</strong>
            {" · "}
            {p.board_name} · {relPast(item.created_at)}
          </div>
        </div>
      </button>
    );
  }
  if (isRowCommented(item)) {
    const p = item.payload;
    return (
      <button
        type="button"
        onClick={onClick}
        style={rowStyle(unread)}
        onMouseEnter={(e) => (e.currentTarget.style.background = "var(--surface-soft)")}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = unread ? "var(--surface-soft)" : "transparent";
        }}
      >
        <Icon name="bell" size={15} color={unread ? "var(--accent)" : "var(--ink-3)"} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={titleStyle}>
            <span style={{ fontWeight: unread ? 500 : 400 }}>
              <strong style={{ fontWeight: 600 }}>{p.actor_display_name}</strong>
              {" hat auf "}
              <strong style={{ fontWeight: 600 }}>{p.row_title || "Anfrage"}</strong>
              {" kommentiert"}
            </span>
          </div>
          <div style={subStyle}>
            {p.body_excerpt} · {p.board_name} · {relPast(item.created_at)}
          </div>
        </div>
      </button>
    );
  }
  if (isRowAssigned(item)) {
    const p = item.payload;
    return (
      <button
        type="button"
        onClick={onClick}
        style={rowStyle(unread)}
        onMouseEnter={(e) => (e.currentTarget.style.background = "var(--surface-soft)")}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = unread ? "var(--surface-soft)" : "transparent";
        }}
      >
        <Icon name="person" size={15} color={unread ? "var(--accent)" : "var(--ink-3)"} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={titleStyle}>
            <span style={{ fontWeight: unread ? 500 : 400 }}>
              {t.notificationAssigned}
              {": "}
              <strong style={{ fontWeight: 600 }}>{p.row_title || "Anfrage"}</strong>
            </span>
          </div>
          <div style={subStyle}>
            {p.actor_display_name} · {p.board_name} · {relPast(item.created_at)}
          </div>
        </div>
      </button>
    );
  }
  if (isTicketArrived(item)) {
    const p = item.payload;
    return (
      <button
        type="button"
        onClick={onClick}
        style={rowStyle(unread)}
        onMouseEnter={(e) => (e.currentTarget.style.background = "var(--surface-soft)")}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = unread ? "var(--surface-soft)" : "transparent";
        }}
      >
        <Icon name="inbox" size={15} color={unread ? "var(--accent)" : "var(--ink-3)"} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={titleStyle}>
            <span style={{ fontWeight: unread ? 500 : 400 }}>
              {t.notificationTicketArrived}
              {": "}
              <strong style={{ fontWeight: 600 }}>{p.row_title || "Anfrage"}</strong>
            </span>
          </div>
          <div style={subStyle}>
            {p.actor_display_name} · {p.board_name} · {relPast(item.created_at)}
          </div>
        </div>
      </button>
    );
  }
  if (isDeadlineReminder(item)) {
    const p = item.payload;
    const urgent = p.days_remaining <= 2;
    return (
      <button
        type="button"
        onClick={onClick}
        style={rowStyle(unread)}
        onMouseEnter={(e) => (e.currentTarget.style.background = "var(--surface-soft)")}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = unread ? "var(--surface-soft)" : "transparent";
        }}
      >
        <Icon
          name="date"
          size={15}
          color={urgent ? "#C2410C" : unread ? "var(--accent)" : "var(--ink-3)"}
        />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={titleStyle}>
            <span style={{ fontWeight: unread ? 500 : 400 }}>
              <strong style={{ fontWeight: 600 }}>{p.row_title || "Aufgabe"}</strong>
              {" — "}
              {deadlineText(p.days_remaining)}
            </span>
          </div>
          <div style={subStyle}>
            {p.deadline} · {p.board_name} · {relPast(item.created_at)}
          </div>
        </div>
      </button>
    );
  }
  if (isRowShared(item)) {
    const p = item.payload;
    return (
      <button
        type="button"
        onClick={onClick}
        style={rowStyle(unread)}
        onMouseEnter={(e) => (e.currentTarget.style.background = "var(--surface-soft)")}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = unread ? "var(--surface-soft)" : "transparent";
        }}
      >
        <Icon name="share" size={15} color={unread ? "var(--accent)" : "var(--ink-3)"} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={titleStyle}>
            <span style={{ fontWeight: unread ? 500 : 400 }}>
              {p.via_department ? t.notificationSharedDept : t.notificationShared}
              {": "}
              <strong style={{ fontWeight: 600 }}>{p.row_title || "Ticket"}</strong>
            </span>
          </div>
          <div style={subStyle}>
            {p.actor_display_name} · {p.board_name} · {relPast(item.created_at)}
          </div>
        </div>
      </button>
    );
  }
  if (isFollowUpCreated(item)) {
    const p = item.payload;
    return (
      <button
        type="button"
        onClick={onClick}
        style={rowStyle(unread)}
        onMouseEnter={(e) => (e.currentTarget.style.background = "var(--surface-soft)")}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = unread ? "var(--surface-soft)" : "transparent";
        }}
      >
        <Icon name="link" size={15} color={unread ? "var(--accent)" : "var(--ink-3)"} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={titleStyle}>
            <span style={{ fontWeight: unread ? 500 : 400 }}>
              {t.notificationFollowUp}
              {": "}
              <strong style={{ fontWeight: 600 }}>{p.row_title || "Ticket"}</strong>
            </span>
          </div>
          <div style={subStyle}>
            {p.actor_display_name}
            {p.follow_up_label ? ` · ${p.follow_up_label}` : ""} · {p.board_name} ·{" "}
            {relPast(item.created_at)}
          </div>
        </div>
      </button>
    );
  }
  if (isMemoNotification(item)) {
    const p = item.payload;
    const label =
      item.kind === "memo.finalized" ? t.notificationMemoFinalized : t.notificationMemoPrep;
    return (
      <button
        type="button"
        onClick={onClick}
        style={rowStyle(unread)}
        onMouseEnter={(e) => (e.currentTarget.style.background = "var(--surface-soft)")}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = unread ? "var(--surface-soft)" : "transparent";
        }}
      >
        {/* The same notebook the Protokolle rail uses — a bell here would say "generic thing". */}
        <Icon name="notebook-text" size={15} color={unread ? "var(--accent)" : "var(--ink-3)"} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={titleStyle}>
            <span style={{ fontWeight: unread ? 500 : 400 }}>
              {label}
              {": "}
              <strong style={{ fontWeight: 600 }}>{p.memo_title || "Protokoll"}</strong>
            </span>
          </div>
          <div style={subStyle}>
            {p.actor_display_name}
            {p.memo_date ? ` · ${p.memo_date}` : ""} · {relPast(item.created_at)}
          </div>
        </div>
      </button>
    );
  }

  return (
    <button type="button" onClick={onClick} style={rowStyle(unread)}>
      <Icon name="bell" size={15} color="var(--ink-3)" />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={titleStyle}>{item.kind}</div>
        <div style={subStyle}>{relPast(item.created_at)}</div>
      </div>
    </button>
  );
}

function Empty({ label }: { label: string }) {
  return (
    <div
      style={{
        padding: "32px 16px",
        textAlign: "center",
        color: "var(--ink-3)",
        fontSize: 13,
      }}
    >
      {label}
    </div>
  );
}

function rowStyle(unread: boolean): CSSProperties {
  return {
    display: "flex",
    alignItems: "flex-start",
    gap: 10,
    width: "100%",
    padding: "12px 14px",
    border: "none",
    borderLeft: `3px solid ${unread ? BRAND.blue : "transparent"}`,
    background: unread ? "var(--surface-soft)" : "transparent",
    cursor: "pointer",
    textAlign: "left",
    fontFamily: "Inter Variable, Inter, system-ui, sans-serif",
    transition: "background .08s",
  };
}

const titleStyle: CSSProperties = {
  fontSize: 13,
  color: "var(--ink-1)",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  marginBottom: 3,
};

const subStyle: CSSProperties = {
  fontSize: 11.5,
  color: "var(--ink-3)",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};
